import { useEffect } from "react";
import { Form, useActionData, useNavigation, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import { Button } from "../../components/common";
import { LanguageSwitcher } from "../../components/common/LanguageSwitcher";
import type { ActionResult } from "../../routes/actions";
import "./auth.css";

export default function LoginPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigation = useNavigation();
  const { refetchUser } = useAuth();
  const actionData = useActionData() as ActionResult | undefined;
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || "/dashboard";
  const isSubmitting = navigation.state === "submitting";

  useEffect(() => {
    if (!actionData) return;

    if (actionData.success) {
      toast.success(t("auth.login.success"));
      refetchUser().then(() => {
        window.location.href = from;
      });
    } else if (actionData.error) {
      toast.error(actionData.error);
    }
  }, [actionData, refetchUser, from, t]);

  return (
    <div className="auth-container">
      <div className="auth-language-switcher">
        <LanguageSwitcher variant="standalone" />
      </div>
      <div className="auth-card">
        <div className="auth-header">
          <h1>{t("auth.login.title")}</h1>
          <p>{t("auth.login.subtitle")}</p>
        </div>

        <Form method="post" className="auth-form">
          <div className={`form-group ${actionData?.fieldErrors?.email ? "error" : ""}`}>
            <label htmlFor="email">{t("auth.login.emailLabel")}</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder={t("auth.login.emailPlaceholder")}
              autoComplete="email"
            />
            {actionData?.fieldErrors?.email && (
              <span className="form-error">{actionData.fieldErrors.email}</span>
            )}
          </div>

          <div className={`form-group ${actionData?.fieldErrors?.password ? "error" : ""}`}>
            <label htmlFor="password">{t("auth.login.passwordLabel")}</label>
            <input
              id="password"
              name="password"
              type="password"
              placeholder={t("auth.login.passwordPlaceholder")}
              autoComplete="current-password"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.currentTarget.form?.requestSubmit();
                }
              }}
            />
            {actionData?.fieldErrors?.password && (
              <span className="form-error">{actionData.fieldErrors.password}</span>
            )}
          </div>

          <Button
            type="submit"
            variant="primary"
            fullWidth
            loading={isSubmitting}
            loadingText={t("auth.login.submitting")}
          >
            {t("auth.login.submitButton")}
          </Button>
        </Form>

        <div className="auth-links">
          <Link to="/forgot-password" className="auth-link">
            {t("auth.login.forgotPassword")}
          </Link>
          <div className="auth-divider">{t("common.or")}</div>
          <Link to="/register" className="auth-link">
            {t("auth.login.noAccount")}
          </Link>
        </div>
      </div>
    </div>
  );
}
